import { useCallback, useState } from "react"

import { UPLOAD_CONCURRENCY_LIMIT } from "@/components/upload/constants"
import { UploadRowStatus } from "@/components/upload/MultiFileUpload/types"
import { type UploadFailure, getUploadOutcomeNotification } from "@/components/upload/MultiFileUpload/utils"
import type { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFileUpload } from "@/hooks/useFileUpload"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { mapWithConcurrency } from "@/utils/mapWithConcurrency"

export interface UploadFileItem {
  id: string
  file: File
  fieldGroup: FieldGroupEnum
  label?: string
}

interface UploadFilesParams {
  referenceIds: string[]
  totalFailureMessage?: string
}

export interface UploadSummary {
  statuses: Record<string, UploadRowStatus>
  failures: UploadFailure[]
}

interface ItemProgress {
  completed: number
  failed: number
  total: number
}

export interface UploadRowProgress {
  completed: number
  total: number
}

interface UseMultiFileUploadParams {
  referenceTableName: ReferenceTableName
}

interface UseMultiFileUploadReturn {
  statuses: Record<string, UploadRowStatus>
  progress: Record<string, UploadRowProgress>
  isUploading: boolean
  uploadFiles: (items: UploadFileItem[], params: UploadFilesParams) => Promise<UploadSummary>
}

const toRowProgress = ({ completed, failed, total }: ItemProgress): UploadRowProgress => ({
  completed: completed + failed,
  total,
})

const getRowStatus = ({ completed, failed, total }: ItemProgress): UploadRowStatus => {
  if (completed + failed === 0) return UploadRowStatus.Pending
  if (completed + failed < total) return UploadRowStatus.Uploading
  return failed > 0 ? UploadRowStatus.Failed : UploadRowStatus.Uploaded
}

export const useMultiFileUpload = ({ referenceTableName }: UseMultiFileUploadParams): UseMultiFileUploadReturn => {
  const { uploadFile } = useFileUpload()
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [statuses, setStatuses] = useState<Record<string, UploadRowStatus>>({})
  const [progress, setProgress] = useState<Record<string, UploadRowProgress>>({})
  const [isUploading, setIsUploading] = useState(false)

  const uploadFiles = useCallback(
    async (
      items: UploadFileItem[],
      { referenceIds, totalFailureMessage }: UploadFilesParams
    ): Promise<UploadSummary> => {
      if (!items.length || !referenceIds.length) {
        return { statuses: {}, failures: [] }
      }

      setIsUploading(true)
      setStatuses(Object.fromEntries(items.map((item) => [item.id, UploadRowStatus.Pending])))
      setProgress(Object.fromEntries(items.map((item) => [item.id, { completed: 0, total: referenceIds.length }])))

      const progressByItemId: Record<string, ItemProgress> = Object.fromEntries(
        items.map((item) => [item.id, { completed: 0, failed: 0, total: referenceIds.length }])
      )

      const units = items.flatMap((item) => referenceIds.map((referenceId) => ({ item, referenceId })))

      await mapWithConcurrency(units, UPLOAD_CONCURRENCY_LIMIT, async ({ item, referenceId }) => {
        const success = await uploadFile({
          file: item.file,
          type: item.fieldGroup,
          label: item.label ?? "",
          metadata: {
            fieldGroup: item.fieldGroup,
            referenceId,
            referenceTableName,
          },
        })

        const itemProgress = progressByItemId[item.id]
        itemProgress.completed += success ? 1 : 0
        itemProgress.failed += success ? 0 : 1
        setStatuses((prev) => ({ ...prev, [item.id]: getRowStatus(itemProgress) }))
        setProgress((prev) => ({ ...prev, [item.id]: toRowProgress(itemProgress) }))

        return success
      })

      setIsUploading(false)

      const finalStatuses: Record<string, UploadRowStatus> = {}
      const failures: UploadFailure[] = []
      for (const item of items) {
        const status = getRowStatus(progressByItemId[item.id])
        finalStatuses[item.id] = status
        if (status === UploadRowStatus.Failed) {
          failures.push({ id: item.id, fileName: item.file.name })
        }
      }

      const outcomeNotification = getUploadOutcomeNotification(failures, items.length, totalFailureMessage)
      if (outcomeNotification) {
        notify(outcomeNotification)
      }

      return { statuses: finalStatuses, failures }
    },
    [uploadFile, referenceTableName, notify]
  )

  return { statuses, progress, isUploading, uploadFiles }
}
